import React, { useState, useCallback, useEffect, ReactNode, useMemo } from 'react';
import { Box, Card, Typography, Tooltip, useTheme, LinearProgress, IconButton, styled } from '@mui/material';
import DatasetIcon from '@mui/icons-material/Storage';
import { useTranslation } from 'react-i18next';
import { RootState, useAppDispatch, useAppSelector } from '../../store/store';
import { handleTesterRowClick, handleToggleFileDetailsPopup, handleToggleDeleteFilePopup, handleToggleFileDatasetPopup } from '../../store/mltester-slice/mltester';
import { DownloadFile } from '../../services/FileServices';
import { EndPoints } from '../../axios/endPoints';
import MLTester from './MLTester';
import SelectedMLTester from './SelectedMLTester';
import { motion, AnimatePresence } from 'framer-motion';
import moment from 'moment';
import PopoverTooltip from '../common/PopoverTooltip';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import DatasetTwoToneIcon from '@mui/icons-material/DatasetTwoTone';
import CloudDownloadTwoToneIcon from '@mui/icons-material/CloudDownloadTwoTone';
import DeleteForeverTwoToneIcon from '@mui/icons-material/DeleteForeverTwoTone';
import DeleteFilePopup from './DeleteFilePopup';
import TesterFileDetails from './TesterFileDetails';
import CreatorFileDetails from '../ml-creator/CreatorFileDetails';
import BugReportIcon from '@mui/icons-material/BugReport';
import TesterFileData from './TesterFileData';

interface StepDef {
    labelKey: string;
    defaultLabel: string;
    IconComponent: React.ComponentType;
}

const steps: StepDef[] = [
    {
        labelKey: 'TESTER_STEPPER_DATASETS_LABEL',
        defaultLabel: 'Testing Datasets',
        IconComponent: DatasetIcon,
    },
    {
        labelKey: 'TESTER_STEPPER_TESTING_MODELS_LABEL',
        defaultLabel: 'Training Models',
        IconComponent: BugReportIcon,
    },
];

const StepCircle = styled(Box)(({ theme }) => ({
    width: 64,
    height: 64,
    [theme.breakpoints.down('sm')]: {
        width: 48,
        height: 48,
    },
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[2],
    transition: 'all 0.3s ease',
}));

const stepMotion = {
    initial: { opacity: 0, y: 30, scale: 0.98 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: -30, scale: 0.95 },
    transition: { duration: 0.4, ease: 'easeInOut' },
};

const TesterDatasetTooltipContent: React.FC<{
    row: any;
    onDataset: () => void;
    onDetails: () => void;
    onDownload: () => void;
    onDelete: () => void;
}> = ({ row, onDataset, onDetails, onDownload, onDelete }) => {
    const { t } = useTranslation();

    return (
        <Box
            sx={{
                p: 2,
                maxWidth: 280,
                backgroundColor: 'white',
                borderRadius: 2,
                boxShadow: 3,
                border: '1px solid #e0e0e0',
            }}
        >
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                {t('TESTER_ID_FIELD')}: {row.id}
            </Typography>
            <Typography variant="body2">{t('TESTER_NAME_FIELD')}: {row.name}</Typography>
            <Typography variant="body2">{t('TESTER_FILE_NAME_FIELD')}: {row.fileName}</Typography>
            <Typography variant="body2">{t('TESTER_ROW_COUNT_FIELD')}: {row.rowCount}</Typography>
            <Typography variant="body2">{t('TESTER_COLUMN_COUNT_FIELD')}: {row.columnCount}</Typography>
            <Typography variant="body2">
                {t('TESTER_UPLOAD_DATE_FIELD')}:{' '}
                {moment.utc(row.uploadDate).local().format('DD/MM/YYYY HH:mm')}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Tooltip title={t('TESTER_DATA_ACTION')} arrow>
                    <IconButton size="small" onClick={onDataset}>
                    <DatasetTwoToneIcon className="dataset-icon" fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title={t('TESTER_DETAILS_ACTION')} arrow>
                    <IconButton size="small" onClick={onDetails}>
                        <InfoTwoToneIcon className="info-icon" fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title={t('TESTER_DOWNLOAD_ACTION')} arrow>
                    <IconButton size="small" onClick={onDownload}>
                        <CloudDownloadTwoToneIcon className="download-icon" fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title={t('TESTER_DELETE_ACTION')} arrow>
                    <IconButton size="small" onClick={onDelete}>
                        <DeleteForeverTwoToneIcon className="delete-icon" fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
};

const MLTesterSwitcher: React.FC = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const selectedTesterRow = useAppSelector((state: RootState) => state.mltester.selectedTesterRow);

    const [activeStep, setActiveStep] = useState<number>(0);

    const goToStep = useCallback(
        (stepIndex: number, rowData?: any) => {
            if (stepIndex === 0) {
                dispatch(handleTesterRowClick(null));
                setActiveStep(0);
            } else if (stepIndex === 1 && rowData) {
                dispatch(handleTesterRowClick(rowData));
                setActiveStep(1);
            }
        },
        [dispatch]
    );

    useEffect(() => {
        return () => {
            dispatch(handleTesterRowClick(null));
            setActiveStep(0);
        };
    }, [dispatch]);

    const datasetTooltip = useMemo<ReactNode | null>(() => {
        if (!selectedTesterRow) return null;

        return (
            <TesterDatasetTooltipContent
                row={selectedTesterRow}
                onDataset={() =>
                    dispatch(
                    handleToggleFileDatasetPopup({
                        open: true,
                        body: { datasetId: selectedTesterRow.id, pageNumber: 0, pageSize: 10}
                    })
                    )
                }
                onDetails={() =>
                    dispatch(
                        handleToggleFileDetailsPopup({
                            open: true,
                            body: { fileId: selectedTesterRow.id, fileName: selectedTesterRow.fileName },
                        })
                    )
                }
                onDownload={() =>
                    DownloadFile(`${EndPoints.ML_TESTER_DOWNLOAD_TESTING_DATASET}?id=${selectedTesterRow.id}`)
                }
                onDelete={() =>
                    dispatch(
                        handleToggleDeleteFilePopup({
                            open: true,
                            body: {
                                fileName: selectedTesterRow.fileName,
                                fileId: selectedTesterRow.id,
                            },
                        })
                    )
                }
            />
        );
    }, [selectedTesterRow, dispatch]);

    return (
        <>
            <Card
                sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: { xs: 2, sm: 4 },
                    backdropFilter: 'blur(4px)',
                    backgroundColor:
                        theme.palette.mode === 'dark'
                        ? 'rgba(30, 30, 30, 0.6)'
                        : 'rgba(255, 255, 255, 0.8)',
                    boxShadow: theme.shadows[3],
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: { xs: 2, sm: 8 },
                    }}
                >
                    {steps.map((stepDef, idx) => {
                        const isActive = activeStep === idx;
                        const isCompleted = activeStep > idx;
                        const canClick = idx === 0 || (idx === 1 && !!selectedTesterRow);

                        let tooltipContent: ReactNode | undefined;
                        if (idx === 1 && selectedTesterRow) tooltipContent = datasetTooltip;

                        return (
                            <PopoverTooltip
                                key={idx}
                                content={tooltipContent}
                                placement="bottom"
                            >
                                <Box
                                    onClick={() => canClick && goToStep(idx, selectedTesterRow)}
                                    sx={{
                                        cursor: canClick ? 'pointer' : 'not-allowed',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 1,
                                        transform: isActive ? 'scale(1.1)' : 'scale(1)',
                                        transition: 'all 0.3s ease',
                                        opacity: isCompleted || isActive ? 1 : 0.5,
                                    }}
                                >
                                    <StepCircle
                                        sx={{
                                            border: `2px solid ${isActive ? 'var(--button-background-color)' : theme.palette.grey[300]}`,
                                            color: isActive ? 'var(--button-background-color)' : theme.palette.text.primary,
                                            '&:hover': {
                                                transform: canClick ? 'scale(1.15)' : 'none',
                                                color: 'var(--button-background-color)',
                                            },
                                        }}
                                    >
                                        <stepDef.IconComponent />
                                    </StepCircle>

                                    <Typography
                                        variant={isActive ? 'subtitle2' : 'caption'}
                                        fontWeight={isActive ? 'bold' : 'normal'}
                                        textAlign="center"
                                        sx={{ color: isActive ? 'var(--button-background-color)' : undefined }}
                                    >
                                        {t(stepDef.labelKey, stepDef.defaultLabel)}
                                    </Typography>
                                </Box>
                            </PopoverTooltip>
                            );
                        })
                    }
                </Box>

                <Box sx={{ mt: 3 }}>
                    <LinearProgress
                        variant="determinate"
                        value={(activeStep + 1) * (100 / steps.length)}
                        sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: theme.palette.grey[200],
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: 'var(--button-background-color)',
                            },
                        }}
                    />
                </Box>
            </Card>

            <Box
                sx={{
                    position: 'relative',
                    minHeight: { xs: 'auto', sm: 600 },
                    borderRadius: { xs: 2, sm: 4 },
                    overflow: 'hidden',
                    px: { xs: 1, sm: 2 },
                    py: { xs: 1, sm: 2 },
                    mt: 4,
                    boxShadow: theme.shadows[4],
                    backgroundColor: theme.palette.background.default,
                }}
            >
                <AnimatePresence mode="wait">
                    {activeStep === 0 && (
                        <motion.div key="step-0" {...stepMotion}>
                            <MLTester onSelectDataset={(row) => goToStep(1, row)} />
                        </motion.div>
                    )}
                {activeStep === 1 && selectedTesterRow && (
                    <motion.div key="step-1" {...stepMotion}>
                        <SelectedMLTester />
                    </motion.div>
                )}
                </AnimatePresence>
            </Box>
            <DeleteFilePopup onDeleteSuccess={() => goToStep(0)} />
            <CreatorFileDetails />
            <TesterFileDetails />
            <TesterFileData />
        </>
    );
};

export default MLTesterSwitcher;